import React from "react"
import { Container, FlexContainer, SlidingDotsLoader } from "@carrotfertility/carotene"
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService"
import { useCombobox } from "downshift"
import { LocationInput } from "./LocationInput"
import { useConfigContext } from "../../context/ConfigContext"
import { LocationMenu, LocationMenuItem } from "./LocationMenu"
import { useIntl } from "react-intl"

interface LocationPickerProps {
  onInputChange: (inputValue: string) => void
  placeholderText: string
}

export const LocationPicker = ({ onInputChange, placeholderText }: LocationPickerProps): JSX.Element => {
  const debounceRef = React.useRef(null)
  const intl = useIntl()
  const { config } = useConfigContext()
  const { locale } = useIntl()
  const { placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: config.GOOGLE_PLACES_API,
    language: locale // ❗ The langauge will not update if the user changes their locale after the Google Places API loads. See https://carrotfertility.atlassian.net/l/cp/90h5F13n#Doesn%E2%80%99t-update-when-locale-is-changed
  })
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    closeMenu
  } = useCombobox({
    items: placePredictions,
    onInputValueChange: ({ inputValue }) => {
      debounceOnChange(inputValue)
    },
    itemToString: (item) => item?.description
  })

  function debounceOnChange(inputValue: string): void {
    if (!inputValue) {
      closeMenu()
    }
    onInputChange(inputValue)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      getPlacePredictions({ input: inputValue })
    }, 500)
  }

  return (
    <Container sx={{ position: "relative" }}>
      <div {...getComboboxProps()}>
        <LocationInput
          {...getInputProps()}
          placeholderText={placeholderText}
          aria-label={intl.formatMessage({ defaultMessage: "Provider search" })}
        />
      </div>
      <LocationMenu isOpen={isOpen} {...getMenuProps()}>
        {isOpen && !placePredictions.length && (
          <FlexContainer justifyContent="center" alignItems="center" grow={1} width="100%" height="80px">
            <SlidingDotsLoader />
          </FlexContainer>
        )}
        {isOpen &&
          placePredictions.map((item, index) => {
            const { description, matched_substrings: matchedSubstrings } = item
            return (
              <LocationMenuItem
                style={highlightedIndex === index ? { backgroundColor: "#FCF3E5" } : {}}
                key={`${description}-${index}`}
                description={description}
                matchedSubstrings={matchedSubstrings}
                {...getItemProps({ item, index })}
              />
            )
          })}
      </LocationMenu>
    </Container>
  )
}
